import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, User, Calendar, Share2, Twitter, Linkedin, Copy } from 'lucide-react';
import { blogPosts } from '../mockData';
import ReactMarkdown from 'react-markdown';

// Helper to get image URL
const getImageUrl = (image) => {
  if (image && image.startsWith('http')) {
    return image;
  }
  const placeholders = {
    henwy: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80',
    liquidity: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80',
    ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=80',
  };
  return placeholders[image] || 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80';
};

// Generate Article Schema
const generateArticleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": post.title,
  "description": post.excerpt,
  "image": getImageUrl(post.image),
  "author": {
    "@type": "Person",
    "name": post.author,
    "url": "https://trovewealth.com.au/about"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Trove Wealth",
    "logo": {
      "@type": "ImageObject",
      "url": "https://trovewealth.com.au/logo.png"
    }
  },
  "datePublished": "2025-01-01",
  "dateModified": "2025-01-20",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://trovewealth.com.au/blog/${post.id}`
  },
  "articleSection": post.category,
  "keywords": "wealth management, investing, HENWY, Australian finance, " + post.category.toLowerCase()
});

const BlogArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useBetaSignup();
  const post = blogPosts.find(p => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Article Not Found</h1>
          <Link to="/blog">
            <Button className="rounded-full">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : `https://trovewealth.com.au/blog/${post.id}`;
  const articleSchema = generateArticleSchema(post);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOnTwitter = () => {
    const text = `Great read: "${post.title}" by @TroveWealth`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  // Related posts (same category, excluding current)
  const relatedPosts = blogPosts.filter(p => p.category === post.category && p.id !== post.id).slice(0, 2);

  return (
    <div className="min-h-screen bg-white">
      {/* Article Schema for SEO */}
      <Helmet>
        <title>{post.title} | Trove Wealth Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={getImageUrl(post.image)} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px] bg-gray-200 pt-20">
        <img 
          src={getImageUrl(post.image)} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-28 left-4 sm:left-8 flex items-center text-white hover:text-gray-200 transition-colors z-10"
          data-testid="blog-back-btn"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </button>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block px-3 py-1 bg-[#FF8C42] text-white text-xs font-medium rounded-full mb-4">
              {post.category}
            </div>
            <h1 className="text-3xl sm:text-5xl font-light text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center">
                <User size={14} className="mr-1" />
                {post.author}
              </span>
              <span className="flex items-center">
                <Clock size={14} className="mr-1" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Share buttons */}
          <div className="flex items-center gap-3 mb-8 pb-8 border-b">
            <span className="text-sm text-gray-500">Share:</span>
            <button 
              onClick={shareOnTwitter}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Share on Twitter"
            >
              <Twitter size={18} />
            </button>
            <button 
              onClick={shareOnLinkedIn}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={18} />
            </button>
            <button 
              onClick={copyLink}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Copy link"
            >
              <Copy size={18} />
            </button>
          </div>

          {/* Article body */}
          {post.content ? (
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-4xl font-light mt-12 mb-6" style={{ fontFamily: 'Georgia, serif' }} {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-light mt-10 mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }} {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-medium mt-8 mb-3" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }} {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 font-light leading-relaxed mb-6" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-outside ml-6 mb-6 space-y-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-outside ml-6 mb-6 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700 font-light leading-relaxed" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-[#1a1a3e]" {...props} />,
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-[#FF8C42] pl-6 my-8 italic text-gray-600" {...props} />
                  ),
                  table: ({node, ...props}) => (
                    <div className="overflow-x-auto my-8">
                      <table className="min-w-full border-collapse border border-gray-200" {...props} />
                    </div>
                  ),
                  th: ({node, ...props}) => <th className="border border-gray-200 px-4 py-2 bg-gray-50 text-left font-medium" {...props} />,
                  td: ({node, ...props}) => <td className="border border-gray-200 px-4 py-2 text-gray-700 font-light" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 font-light mb-4">{post.excerpt}</p>
              <p className="text-gray-500">Full article coming soon.</p>
            </div>
          )}

          {/* Author bio */}
          <div className="mt-12 pt-8 border-t">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#1a1a3e] rounded-full flex items-center justify-center text-white text-xl font-light">
                {post.author.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="font-medium text-[#1a1a3e]">{post.author}</p>
                <p className="text-sm text-gray-600 font-light">Trove Investment Team</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-light mb-8 text-center" style={{ fontFamily: 'Georgia, serif' }}>
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.id}`}
                  className="group"
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={getImageUrl(relatedPost.image)} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-light group-hover:text-gray-600 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-2">{relatedPost.readTime}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to start building wealth?
          </h2>
          <p className="text-gray-400 mb-8 font-light">
            Join our beta program for early access to institutional-grade investing.
          </p>
          <Button 
            size="lg" 
            onClick={openModal}
            className="bg-[#FF8C42] text-white hover:bg-[#FFB366] px-10 py-7 rounded-full font-light"
          >
            JOIN THE BETA
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
